import { useTranslation } from 'react-i18next';
import { formatTimezoneLabel } from '@/lib/format/timezone';
import { CalendarDays } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { formatCents } from '@/lib/format';
import { rankingPrizePool } from '@/lib/campaigns/rankingCompetition';
import type { CampaignRanking } from '@/queries/cpmRanking';
import { CampaignAvatar } from './CampaignAvatar';
import type { RankingAuditCampaign } from './RankingCampaignAudit';

export function RankingAuditHeader({ campaign, config, currency }: { campaign: RankingAuditCampaign; config: CampaignRanking['config']; currency: string }) {
  const { t, i18n } = useTranslation();
  const locale = i18n?.resolvedLanguage || i18n?.language;
  const paragraph = campaign.description?.trim().split(/\r?\n\s*\r?\n/, 1)[0].replace(/\s+/g, ' ') || '';
  const introduction = paragraph.length > 220 ? `${paragraph.slice(0, 217).replace(/\s+\S*$/, '')}…` : paragraph;
  const start = Date.parse(campaign.start_date || '');
  const end = Date.parse(campaign.end_date || '') - 1;
  const periodStart = Number.isFinite(start) ? new Date(start) : null;
  const periodEnd = Number.isFinite(end) && (!periodStart || end >= start) ? new Date(end) : null;
  const date = (value: Date) => value.toLocaleDateString(locale, { timeZone: config.timezone, dateStyle: 'short' });

  return <Card data-testid="ranking-audit-header">
    <CardContent className="space-y-4 p-4 sm:p-5">
      <div className="flex items-start gap-3 sm:gap-4">
        {campaign.banner_url && <img src={campaign.banner_url} alt={t('ranking.audit.coverAlt', { name: campaign.name })} className="h-20 w-24 shrink-0 rounded-md bg-black object-contain sm:h-24 sm:w-40" />}
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <CampaignAvatar campaign={campaign} className="h-8 w-8 sm:h-12 sm:w-12" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <h2 className="break-words text-lg font-semibold sm:text-xl">{campaign.name}</h2>
              {campaign.status && <Badge variant="outline">{t(`campaignAudit.status.${campaign.status}`)}</Badge>}
            </div>
            <p className="mt-1 line-clamp-2 break-words text-sm text-muted-foreground">{introduction || t('ranking.audit.header.introduction')}</p>
            <p className="mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted-foreground">
              <CalendarDays aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
              <span>{t('ranking.audit.header.period')}:</span>
              {periodStart && <time dateTime={periodStart.toISOString()}>{date(periodStart)}</time>}
              {periodStart && periodEnd && <span>–</span>}
              {periodEnd && <time dateTime={periodEnd.toISOString()}>{date(periodEnd)}</time>}
              <span>· {formatTimezoneLabel(config.timezone)}</span>
            </p>
          </div>
        </div>
      </div>
      <dl className="grid grid-cols-1 gap-3 border-t pt-3 sm:grid-cols-3 sm:gap-5">
        <div><dt className="text-xs text-muted-foreground">{t('ranking.pool')}</dt><dd className="mt-0.5 text-base font-semibold tabular-nums">{formatCents(rankingPrizePool(config), currency)}</dd></div>
        <div><dt className="text-xs text-muted-foreground">{t('ranking.audit.header.minimum')}</dt><dd className="mt-0.5 text-base font-semibold tabular-nums">{config.min_total_views.toLocaleString(locale)}</dd></div>
        <div><dt className="text-xs text-muted-foreground">{t('ranking.payoutDestination')}</dt><dd className="mt-0.5 text-base font-semibold">{t(`ranking.payment.${config.payout_destination}`)}</dd></div>
      </dl>
    </CardContent>
  </Card>;
}
